import React, { useState, useEffect } from 'react';
import axios from 'axios';

const HierarchicalCategorySelector = ({ 
  selectedCategories = [], 
  onChange,
  multiple = true,
  disabled = false 
}) => {
  const [categories, setCategories] = useState([]);
  const [parentCategories, setParentCategories] = useState([]);
  const [childCategories, setChildCategories] = useState({});
  const [selectedParent, setSelectedParent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/categories`);
      const allCategories = response.data;
      
      // Separate parent and child categories
      const parents = allCategories.filter(cat => !cat.parent_id);
      const children = {};
      
      allCategories.forEach(cat => {
        if (cat.parent_id) {
          if (!children[cat.parent_id]) {
            children[cat.parent_id] = [];
          }
          children[cat.parent_id].push(cat);
        }
      });
      
      setCategories(allCategories);
      setParentCategories(parents);
      setChildCategories(children);
      setError(null);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleParentChange = (e) => {
    setSelectedParent(e.target.value);
  };

  const handleChildSelect = (childId) => {
    if (!multiple) {
      // Single select mode
      onChange([childId]);
      setSelectedParent('');
      return;
    }

    // Multiple select mode
    const newSelection = selectedCategories.includes(childId)
      ? selectedCategories.filter(id => id !== childId)
      : [...selectedCategories, childId];
    
    onChange(newSelection);
  };

  const handleParentSelect = () => {
    if (!selectedParent) return;

    if (!multiple) {
      // Single select mode - select parent
      onChange([selectedParent]);
      setSelectedParent('');
      return;
    }

    // Multiple select mode - toggle parent
    const newSelection = selectedCategories.includes(selectedParent)
      ? selectedCategories.filter(id => id !== selectedParent)
      : [...selectedCategories, selectedParent];
    
    onChange(newSelection);
  };

  const handleRemoveCategory = (categoryId) => {
    onChange(selectedCategories.filter(id => id !== categoryId));
  };

  const getCategoryDisplay = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    if (!category) return 'Unknown Category';
    
    // Check if this is a child category
    const parent = categories.find(cat => cat.id === category.parent_id);
    
    if (parent) {
      return `${parent.name} → ${category.name}`;
    }
    
    return category.name;
  };

  if (loading) {
    return <div className="text-sm text-gray-500">Loading categories...</div>;
  }

  if (error) {
    return <div className="text-sm text-red-500">{error}</div>;
  }

  return (
    <div className="space-y-3">
      {/* Selected Categories Display */}
      {multiple && selectedCategories.length > 0 && (
        <div className="flex flex-wrap gap-2 p-2 bg-gray-50 rounded-lg">
          {selectedCategories.map(catId => (
            <div
              key={catId}
              className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
            >
              <span>{getCategoryDisplay(catId)}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemoveCategory(catId)}
                  className="text-blue-600 hover:text-blue-800 font-bold"
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Parent Category Selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {multiple ? 'Select Category' : 'Select Parent Category'}
        </label>
        <select
          value={selectedParent}
          onChange={handleParentChange}
          disabled={disabled}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">-- Choose a category --</option>
          {parentCategories.map(parent => (
            <option key={parent.id} value={parent.id}>
              {parent.name}
            </option>
          ))}
        </select>
      </div>

      {/* Child Categories or Parent Selection Button */}
      {selectedParent && (
        <div className="space-y-2">
          {childCategories[selectedParent] && childCategories[selectedParent].length > 0 ? (
            <>
              <label className="block text-sm font-medium text-gray-700">
                Select Subcategory
              </label>
              <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-white">
                {childCategories[selectedParent].map(child => {
                  const isSelected = selectedCategories.includes(child.id);
                  return (
                    <button
                      type="button"
                      key={child.id}
                      onClick={() => handleChildSelect(child.id)}
                      disabled={disabled}
                      className={`text-left px-3 py-2 rounded transition-colors ${
                        isSelected
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      {child.name}
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-gray-500">No subcategories available</p>
              <button
                type="button"
                onClick={handleParentSelect}
                disabled={disabled}
                className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {selectedCategories.includes(selectedParent) ? 'Deselect' : 'Select'} {parentCategories.find(p => p.id === selectedParent)?.name}
              </button>
            </div>
          )}
        </div>
      )}

      {!multiple && selectedCategories.length > 0 && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm font-medium text-green-800">
            Selected: {getCategoryDisplay(selectedCategories[0])}
          </p>
        </div>
      )}

      {multiple && selectedCategories.length === 0 && (
        <p className="text-sm text-gray-500 italic">
          No categories selected yet
        </p>
      )}
    </div>
  );
};

export default HierarchicalCategorySelector;
